import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

// Async thunk to handle user login with JWT
export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND + "auth/login",
        credentials
      );
      const { name, email, picture } = response.data.data;
      const token = response?.data?.token;
      const user = name;
      localStorage.setItem("token", token);
      localStorage.setItem("name", user);
      localStorage.setItem("userImage", picture);
      return { user, email, token, picture };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  }
);

export const unknownLogin = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const token = credentials;
      localStorage.setItem("token", token);
      localStorage.setItem("name", "Sevenly Traveler");
      return { token };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  }
);

export const signup = createAsyncThunk(
  "auth/signup",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND + "auth/verifyenduser",
        credentials
      );
      const { name, email } = response.data.data;
      const token = response?.data?.token;

      const user = name;
      localStorage.setItem("token", token);
      localStorage.setItem("name", user);
      return { user, token };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);
export const loginByGoogle = createAsyncThunk(
  "auth/loginByGoogle",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND + "auth/googlelogin",
        credentials
      );

      const { name, email, picture } = response.data.data;
      const token = response?.data?.token;

      const user = name;
      localStorage.setItem("token", token);
      localStorage.setItem("name", user);
      localStorage.setItem("userImage", picture);
      return { user, token, picture };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);
export const loginByYahoo = createAsyncThunk(
  "auth/loginByYahoo",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND + "auth/yahoologin",
        credentials
      );

      const { name, email, picture } = response.data.data;
      const token = response?.data?.token;

      const user = name;
      localStorage.setItem("token", token);
      localStorage.setItem("name", user);
      localStorage.setItem("userImage", picture);
      return { user, token, picture };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

// Async thunk to fetch user data (for maintaining login state on reload)
export const fetchUser = createAsyncThunk(
  "auth/fetchUser",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) throw new Error("No token found");

      // Include token in the request header
      const response = await axios.get("/api/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

// Initial state
const initialState = {
  user: localStorage.getItem("name") || null,
  token: localStorage.getItem("token") || null,
  picture: localStorage.getItem("userImage") || null,
  error: null,
  loading: false,
};

// Auth slice definition
export const auth = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.error = null;
      state.picture = null;

      // Remove token from local storage
      localStorage.removeItem("token");
      localStorage.removeItem("name");
      localStorage.removeItem("userImage");
      localStorage.setItem("logout", Date.now().toString());
    },

    // New action to update the user's profile picture and name
    updateUserProfile: (state, action) => {
      const { name, picture } = action.payload;
      state.user = name;
      state.picture = picture;

      // Update local storage for persistence
      localStorage.setItem("name", name);
      localStorage.setItem("userImage", picture);
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle login cases
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.picture = action.payload.picture;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.picture = action.payload.picture;
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loginByGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginByGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.picture = action.payload.picture;
      })
      .addCase(loginByGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.token = null; // Clear token if fetch user fails
        localStorage.removeItem("token");
      })
      .addCase(loginByYahoo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginByYahoo.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.picture = action.payload.picture;
      })
      .addCase(loginByYahoo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.token = null; // Clear token if fetch user fails
        localStorage.removeItem("token");
        localStorage.removeItem("yahoo_oauth_state");
        localStorage.removeItem("yahoo_code_verifier");
      });
  },
});

// Export actions and reducer
export const { logout, updateUserProfile } = auth.actions;
export default auth.reducer;
